import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../redux/reducers/index";
import config from "../config/enviroment";
import { shouldShowMarcaBlancaModal } from "../components/MarcaBlancaModal/shouldShow";
import { blockMarcaBlanca } from "../redux/actions/marcaBlancaActions";
import useMarcaBlancaVerify from "./useMarcaBlancaVerify";
import { getHashFromUrl } from "../utils/marcaBlancaHash";

// Chokepoints que solo deben ser accesibles tras validar: abrir un producto
// (product-info) e iniciar el checkout (carrito). Case-insensitive porque
// react-router no distingue mayúsculas en las rutas.
// NOTA: `purchase` (confirmación post-pago) NO se protege: ePayco redirige ahí
// con una recarga completa y, como el estado marcaBlanca no se persiste, el
// guard bloquearía la vista de resultado y mandaría a la landing.
const PROTECTED_ROUTE = /\/(product-info|carrito)(\/|$)/i;

const getIdCatalog = (pathname: string): string | null => {
  const match = pathname.match(/\/vende\/([^/]+)/);
  return match ? match[1] : null;
};

/**
 * Guardia de acceso para la tienda de Datáfonos Marca Blanca.
 *
 * En cada navegación a una ruta protegida re-verifica la validación contra el
 * backend con las credenciales que el cliente ingresó en el modal. Si nunca
 * validó (p. ej. borró el modal del DOM) o el backend responde distinto de
 * "Success", lo devuelve al Home y reabre el modal. La verdad la da el backend
 * en cada intento, por lo que manipular el DOM no permite avanzar.
 */
const useMarcaBlancaGuard = (history: any) => {
  const dispatch = useDispatch();
  const clientState: any = useSelector((state: RootState) => state.client);
  const marcaBlanca = useSelector((state: RootState) => state.marcaBlanca);
  const { verify } = useMarcaBlancaVerify();

  const gated = shouldShowMarcaBlancaModal(
    clientState?.id,
    config.marcaBlancaId
  );

  useEffect(() => {
    if (!gated) return;

    const enforce = async (pathname: string) => {
      if (!PROTECTED_ROUTE.test(pathname)) return;

      const goHome = () => {
        const idCatalog = getIdCatalog(pathname);
        history.replace(idCatalog ? `/vende/${idCatalog}` : "/");
      };

      if (
        marcaBlanca.hashAuthStatus === "validating" ||
        (getHashFromUrl() &&
          !marcaBlanca.validated &&
          marcaBlanca.hashAuthStatus === "idle")
      ) {
        return;
      }

      // Nunca validó o borró el modal sin validar: no hay credenciales.
      if (
        !marcaBlanca.validated ||
        !marcaBlanca.clientId ||
        !marcaBlanca.documentNumber
      ) {
        dispatch(blockMarcaBlanca());
        goHome();
        return;
      }

      // Re-verifica contra el backend. Solo bloquea ante un rechazo explícito
      // (Rejected); ante un error de red confía en la validación previa para no
      // bloquear a un usuario legítimo.
      const { ok, errored } = await verify({
        clientId: marcaBlanca.clientId,
        documentNumber: marcaBlanca.documentNumber,
        documentTypeId: marcaBlanca.documentTypeId,
      });
      if (!ok && !errored) {
        dispatch(blockMarcaBlanca());
        goHome();
      }
    };

    enforce(history.location.pathname);
    const unlisten = history.listen((location: any) =>
      enforce(location.pathname)
    );
    return unlisten;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gated,
    marcaBlanca.validated,
    marcaBlanca.clientId,
    marcaBlanca.documentNumber,
    marcaBlanca.hashAuthStatus,
  ]);
};

export default useMarcaBlancaGuard;
